import React, { useRef } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import ChartWrapper from "./ChartWrapper";

const PdfDownloadComponent = () => {
  const chartRef = useRef(null);

  const downloadPDF = async () => {
    const chart = chartRef.current;
    if (!chart) return;

    // Get a full-size SVG clone (3000×2000 user units) — independent of
    // whatever zoom/pan the user has applied on screen.
    const nativeSvg = chart.getFullSizeSVG?.();
    if (!nativeSvg) {
      alert("चार्ट लोड नहीं हुआ। कृपया पुनः प्रयास करें।");
      return;
    }

    // ---- Off-screen container at the SVG's TRUE native pixel size ----
    // This is the key fix: html2canvas was previously constrained by the
    // on-screen panel size. By rendering the SVG into a 3000×2000 box
    // off-screen, we guarantee every detail is captured.
    const NATIVE_W = 3000;
    const NATIVE_H = 2000;

    const offscreen = document.createElement("div");
    offscreen.style.position = "fixed";
    offscreen.style.left = "-99999px";
    offscreen.style.top = "0";
    offscreen.style.width = NATIVE_W + "px";
    offscreen.style.height = NATIVE_H + "px";
    offscreen.style.background = "#ffffff";
    offscreen.appendChild(nativeSvg);
    document.body.appendChild(offscreen);

    try {
      // scale: 2 → final raster is 6000×4000 px. Crisp at any zoom level
      // in a PDF viewer, and Hindi text stays readable.
      const canvas = await html2canvas(offscreen, {
        scale: 2,
        backgroundColor: "#ffffff",
        width: NATIVE_W,
        height: NATIVE_H,
        windowWidth: NATIVE_W,
        windowHeight: NATIVE_H,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92); // JPEG = much
      // smaller file than PNG at this resolution, no visible quality loss
      // for this kind of flat-color vector content.

      // ---- Single-page large-format PDF ----
      // A2 landscape (594 × 420 mm) — standard large-format size, aspect
      // ratio (1.414) is close to the tree's (1.5). Whole tree fits on ONE
      // page. User zooms in the PDF viewer to read details; no slicing,
      // no clipping, no multi-page hunting.
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a2",   // 594 × 420 mm
      });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 12;

      // Fit image to page width, preserve aspect ratio, center vertically.
      const imgW = pageW - 2 * margin;
      const imgH = (canvas.height * imgW) / canvas.width;
      const y = (pageH - imgH) / 2;

      pdf.addImage(imgData, "JPEG", margin, y, imgW, imgH);

      // Document metadata (shows up in PDF reader's "Properties" dialog).
      pdf.setProperties({
        title: "चतुर्वेदी वंश-वृक्ष",
        subject: "Chaturvedi Family Tree",
        author: "Chaturvedi Parivar",
      });

      pdf.save("chaturvedi-vansh-vriksh.pdf");
    } finally {
      // Always clean up the off-screen node, even if capture failed.
      document.body.removeChild(offscreen);
    }
  };

  return (
    <div className="page">
      {/* ---------- Compact masthead ---------- */}
      <header className="masthead">
        <div className="masthead-main">
          <div className="masthead-eyebrow">
            चतुर्वेदी परिवार · स्थापित १८५०
          </div>
          <h1 className="masthead-title">
            चतुर्वेदी <em>वंश-वृक्ष</em>
          </h1>
          <p className="masthead-lede">
            कई पीढ़ियों की यादों, मजबूत रिश्तों और गौरवशाली इतिहास के
            ताने-बाने से बुना हुआ चतुर्वेदी परिवार का डिजिटल स्वरूप —
            जो हमारी अमर विरासत को सादर समर्पित है।
          </p>
        </div>
        <div className="masthead-stats">
          <div><strong>६+</strong><span>पीढ़ियाँ</span></div>
          <div><strong>५०+</strong><span>सदस्य</span></div>
          <div><strong>१८५०</strong><span>प्रारंभिक रिकॉर्ड</span></div>
        </div>
      </header>

      {/* ---------- Action row (between header and chart) ---------- */}
      <div className="action-row">
        <p className="prose-lead">
          यह डिजिटल वंश-वृक्ष चतुर्वेदी परिवार की विरासत और आपसी रिश्तों को
          कई पीढ़ियों तक समेटे हुए है — उन नामों को सहेजने का प्रयास,
          जो वक्त के साथ विस्मृति में खो जाते।
        </p>
        <button className="btn-primary" onClick={downloadPDF}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          पीडीएफ
        </button>
      </div>

      {/* ---------- Chart ---------- */}
      <div>
        <div className="section-head">
          <h2>वंश-वृक्ष</h2>
          <div className="legend">
            <span><span className="legend-dot alive" />जीवित</span>
            <span><span className="legend-dot deceased" />स्वर्गवासी</span>
            <span className="meta">विवरण हेतु क्लिक करें · ज़ूम हेतु स्क्रॉल करें</span>
          </div>
        </div>

        <ChartWrapper ref={chartRef} />
      </div>
    </div>
  );
};

export default PdfDownloadComponent;
